/**
 * Pure helpers behind the filter bar's two-level Geography filter.
 *
 * dim_region is a flat two-level denormalization: region_name is the city and the
 * nullable `state` column is the province it rolls up to. These functions derive the
 * province list and the province→city cascade from a plain region list, kept out of
 * FilterBar so the logic is testable without a DOM.
 */

/**
 * Province a region rolls up to. Mirrors backend.region_rollup_sql() — `state` is
 * nullable, so fall back to the city name rather than dropping the row.
 */
export const rollup = r => ((r.state || '').trim() || r.region_name);

/** Sorted, de-duplicated province list. */
export function deriveProvinces(regions) {
  return [...new Set(regions.map(rollup))].sort((a, b) => a.localeCompare(b));
}

/**
 * Whether a Province control is worth showing.
 *
 * Some orgs only ever collected data at one granularity — dcm's dim_region holds
 * Indian states in BOTH region_name and state, so the province list would just be the
 * city list again. Offering a filter that narrows nothing is worse than not offering
 * it. Same idea as the Entity Explorer's cityDistinct flag, decided client-side
 * because the data needed to decide it is already here.
 */
export function isProvinceDistinct(regions, provinces) {
  return provinces.length > 0 && provinces.length < regions.length;
}

/** Regions inside the chosen provinces; all of them when no province is chosen. */
export function scopedCities(regions, selectedProvinces) {
  const picked = new Set(selectedProvinces);
  if (!picked.size) return regions;
  return regions.filter(r => picked.has(rollup(r)));
}

/**
 * Drop selected cities that fell outside a new province selection, so the two
 * controls never disagree. Display-level only — the server enforces the province
 * itself off the `province` param.
 */
export function pruneCities(regions, selectedProvinces, selectedCityIds) {
  if (!selectedProvinces.length) return selectedCityIds;
  const allowed = new Set(scopedCities(regions, selectedProvinces).map(r => r.region_id));
  return selectedCityIds.filter(id => allowed.has(id));
}

/** Order-sensitive equality for a filter selection, treating null as empty. */
export function sameSelection(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.length === b.length && a.every((v, i) => v === b[i]);
}
